import React, {useCallback, useState} from "react";
import {useDropzone} from "react-dropzone";
import {Button, Card} from "react-bootstrap";
import ProgressBar from "react-bootstrap/ProgressBar";
import CreatableSelect from "react-select/creatable";
import {useTranslation} from "react-i18next";
import {MultiValue} from "react-select";
import {IOptionType} from "../../types/common";
import "./PDFUploadCard.scss";

interface IPDFUploadCardProps {
    isUploadCompleted: boolean;
    isUploading: boolean;
    progress: number;
    onUploadFiles: (files: File[]) => void;
}

export const PDFUploadCard: React.FC<IPDFUploadCardProps> = ({
                                                                 isUploadCompleted,
                                                                 isUploading,
                                                                 progress,
                                                                 onUploadFiles,
                                                             }) => {
    const {t} = useTranslation();
    const [files, setFiles] = useState<File[]>([]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
    }, []);

    const {getRootProps, getInputProps} = useDropzone({
        accept: {"application/pdf": [".pdf"]},
        multiple: true,
        onDrop,
    });

    const handleFilesChange = (selectedOptions: MultiValue<IOptionType>) => {
        const selectedFiles = selectedOptions.map((option) =>
            files.find((file) => file.name === option.value)
        );
        setFiles(files.filter((file) => selectedFiles.includes(file)));
    };

    return (
        <Card className="upload-pdf-card">
            <Card.Body>
                {!isUploadCompleted && (
                    <div>
                        <div {...getRootProps()} className="text-center p-4 border-3 dropzone-custom">
                            <input {...getInputProps()} />
                            <p>{isUploadCompleted ? t("PdfFileUpload.UploadSuccess") : t("PdfFileUpload.UploadFile")}</p>
                            <div className="mt-3">
                                <Button variant="secondary" disabled={isUploading || isUploadCompleted}>
                                    {t("Button.selectPdf")}
                                </Button>
                            </div>
                        </div>
                        <CreatableSelect
                            isClearable
                            className="card border-0 d-md-block mt-3"
                            closeMenuOnSelect={false}
                            options={files.map((file) => ({label: file.name, value: file.name}))}
                            value={files.map((file) => ({label: file.name, value: file.name}))}
                            placeholder={t("PdfFileUpload.SelectedFiles")}
                            isSearchable
                            isMulti
                            onChange={handleFilesChange}
                            noOptionsMessage={() => null}
                        />
                        <div className="d-flex justify-content-center mt-3">
                            <Button
                                variant="secondary"
                                onClick={() => onUploadFiles(files)}
                                disabled={isUploading || isUploadCompleted || files.length === 0}
                            >
                                Upload
                            </Button>
                        </div>
                    </div>
                )}
                {isUploading && (
                    <div className="text-center mt-3">
                        <ProgressBar now={progress} label={`${progress.toFixed(2)}%`}/>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};
